import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { Observable, Subscription } from "rxjs";

import {
    FeatureFlagFacade,
    SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
} from "@hermes/states/flipper";
import {
    GridFacade,
    GridFilter,
    sortQueryParameters,
} from "@hermes/states/grid-filters-state";
import { stopEventPropagation } from "@hermes/utils-generic/helpers";
import { SanitizePipe } from "@hermes/utils-generic/pipes/sanitize";
import { FocusService } from "@hermes/utils-generic/services/focus";

import { AbstractFilterCheckboxComponent } from "../abstract-filter-checkbox.component";

const ARIA_LABEL_ITEM = $localize`:@@hermes.accessibility.item:item`;
const ARIA_LABEL_ITEMS = $localize`:@@hermes.accessibility.items:items`;

/**
 * For filter with simple SVG checkBox
 */
@Component({
    selector: "h-grid-filters-checkbox-crawlable",
    templateUrl: "./grid-filters-checkbox-crawlable.component.html",
    styleUrls: ["./grid-filters-checkbox-crawlable.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFiltersCheckboxCrawlableComponent
    extends AbstractFilterCheckboxComponent
    implements OnChanges, OnDestroy
{
    @Input()
    public filter: GridFilter = undefined;

    @Input()
    public dataTestId: string | undefined;

    public ariaLabel = "";
    public facetQueryParam = {};

    private isCombinedFacetEnabled$: Observable<boolean> =
        this.featureFlagFacade.isActivated(
            SEO_COMBINED_FACETS_ACTIVATION_ON_GRID,
        );

    private isCombinedFacetActivated: undefined | boolean = undefined;

    private subscription: Subscription = new Subscription();

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        public elementReference: ElementRef,
        public sanitizePipe: SanitizePipe,
        public focusService: FocusService,
        public cd: ChangeDetectorRef,
        public gridFacade: GridFacade,
        private featureFlagFacade: FeatureFlagFacade,
    ) {
        super(elementReference, sanitizePipe, focusService, cd, gridFacade);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes.filter?.currentValue !== changes.filter?.previousValue) {
            this.facetQueryParam = this.getFacetQueryParam();
            this.ariaLabel = this.getAriaLabel(changes.filter.currentValue);
            this.cd.markForCheck();
        }
    }

    public addFilterCrawlableLinks(event: Event): void {
        stopEventPropagation(event);

        // When we click on filtered category, we remove urlParams to avoid to send it to BCK
        this.filter.urlParams = undefined;

        this.filter.selected = true;
        this.filter.filterGroupName = this.filterGroup.facetUrlParameterName;
        this.filter.facetIsCrawlable = true;

        this.gridFacade.updateGridFilter(this.filter, this.filterGroup);

        const indexAvailableElement = this.filtersAvailable.indexOf(
            this.filter,
        );
        this.checkAvailableAndSelectedFilters();

        // To change the focus on the next element
        this.focusOnNextFilterAvailable(
            this.filter,
            this.filtersAvailable,
            indexAvailableElement,
        );

        // If event is from keydown.space
        if (event instanceof KeyboardEvent) {
            this.router.navigate([], {
                queryParams: this.facetQueryParam,
            });
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private getAriaLabel(filter: GridFilter): string {
        if (filter.total > 1) {
            return `${filter.name}, ${filter.total} ${ARIA_LABEL_ITEMS}`;
        }
        return `${filter.name}, ${filter.total} ${ARIA_LABEL_ITEM}`;
    }

    private getFacetQueryParam(): Record<string, string> {
        if (this.isCombinedFacetActivated === undefined) {
            this.subscription.add(
                this.isCombinedFacetEnabled$.subscribe((flag) => {
                    this.isCombinedFacetActivated = flag;
                }),
            );
        }

        const facetQueryParameter = {};
        Object.entries(this.activatedRoute.snapshot.queryParams)
            .filter(([key, _value]) => !key.includes("facet_")) // Remove existing facet
            .filter((key) => !key.includes("page")) // Remove page parameter
            .filter((key) => !key.includes("s")) // Remove search parameter
            .forEach(([key, value]) => (facetQueryParameter[key] = value));

        if (this.filterGroup?.facetUrlParameterName && this.filter) {
            facetQueryParameter[this.filterGroup.facetUrlParameterName] =
                this.filter.value;
        }
        if (this.isCombinedFacetActivated) {
            // We add the already existing facet parameter to the facetQueryParameter
            Object.entries(this.activatedRoute.snapshot.queryParams)
                .filter(([key, _value]) => key.includes("facet_"))
                .forEach(([key, value]) => {
                    if (!facetQueryParameter[key]) {
                        facetQueryParameter[key] = value;
                    }
                });
        }

        return sortQueryParameters(facetQueryParameter);
    }
}
